import { useEffect, useState, Fragment } from "react";
import { api } from "@/lib/api";

const ACTION_LABELS = {
    "student.force_refund": "Force refund",
    "subscription.force_refund": "Force refund",
    "subscription.cancel_at_period_end": "Cancel at period end",
    "pricing.update": "Pricing updated",
    "blog.create": "Blog post created",
    "blog.update": "Blog post updated",
    "blog.delete": "Blog post deleted",
    "site_settings.update": "Site settings updated",
    "phase.unlock": "Phase unlocked",
    "phase.lock": "Phase locked",
    "lesson.update": "Lesson updated",
    "lesson.delete": "Lesson deleted",
    "lesson.create": "Lesson created",
    "module.update": "Module updated",
    "module.create": "Module created",
    "module.delete": "Module deleted",
    "quiz.update": "Quiz updated",
    "question.update": "Question updated",
};

export default function AdminAuditLog() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [action, setAction] = useState("");
    const [targetUserId, setTargetUserId] = useState("");
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [expanded, setExpanded] = useState(null);
    const LIMIT = 50;

    async function load() {
        setLoading(true);
        try {
            const r = await api.adminAuditLog({ action, target_user_id: targetUserId, limit: LIMIT, offset });
            setRows(r.rows);
            setTotal(r.total);
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [offset]);

    return (
        <section className="max-w-5xl mx-auto px-6 py-10 space-y-6" data-testid="admin-audit-log-page">
            <div>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Audit log</div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>All admin actions</h1>
            </div>

            <form
                onSubmit={(e) => { e.preventDefault(); setOffset(0); load(); }}
                className="st-surface p-4 grid gap-3 md:grid-cols-[1fr_1fr_auto] items-end"
                data-testid="audit-filter-form"
            >
                <div>
                    <label className="st-label">Filter by action (substring)</label>
                    <input className="st-input" value={action} onChange={(e) => setAction(e.target.value)}
                           placeholder="pricing / refund / phase.unlock" data-testid="audit-action-filter" />
                </div>
                <div>
                    <label className="st-label">Target user id</label>
                    <input className="st-input" value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)}
                           placeholder="uuid" data-testid="audit-user-filter" />
                </div>
                <button type="submit" className="btn-royal text-sm" data-testid="audit-apply">Filter</button>
            </form>

            <div className="st-surface overflow-hidden" data-testid="audit-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ background: "var(--st-silver-50)", color: "var(--st-ink-muted)" }}>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">When</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Action</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Target</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Actor</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={5} className="text-center py-8" style={{ color: "var(--st-silver-500)" }}>Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 italic" style={{ color: "var(--st-silver-500)" }}>No entries.</td></tr>}
                        {rows.map((r, i) => (
                            <Fragment key={i}>
                                <tr style={{ borderTop: "1px solid var(--st-line)" }} data-testid={`audit-log-row-${i}`}>
                                    <td className="px-4 py-3 text-xs" style={{ color: "var(--st-silver-500)" }}>
                                        {new Date(r.timestamp).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 font-semibold" style={{ color: "var(--st-navy)" }}>
                                        {ACTION_LABELS[r.action] || r.action}
                                    </td>
                                    <td className="px-4 py-3 text-xs" style={{ color: "var(--st-ink-muted)" }}>
                                        {r.target_user_id ? r.target_user_id.slice(0, 12) + "…" : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs" style={{ color: "var(--st-ink-muted)" }}>
                                        {r.actor_admin_id.slice(0, 12)}…
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button type="button" onClick={() => setExpanded(expanded === i ? null : i)}
                                                className="link-royal text-xs" data-testid={`audit-expand-${i}`}>
                                            {expanded === i ? "Hide" : "Details"}
                                        </button>
                                    </td>
                                </tr>
                                {expanded === i && (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-3" style={{ background: "var(--st-silver-50)" }}>
                                            <pre className="text-xs overflow-x-auto" style={{ color: "var(--st-ink)" }}>
                                                {JSON.stringify(r.detail || {}, null, 2)}
                                            </pre>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs" style={{ color: "var(--st-silver-500)" }}>
                <span>Showing {offset + 1}–{Math.min(offset + rows.length, total)} of {total}</span>
                <div className="flex gap-2">
                    <button type="button" className="btn-ghost text-xs"
                            disabled={offset === 0}
                            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                            data-testid="audit-prev">Previous</button>
                    <button type="button" className="btn-ghost text-xs"
                            disabled={offset + rows.length >= total}
                            onClick={() => setOffset(offset + LIMIT)}
                            data-testid="audit-next">Next</button>
                </div>
            </div>
        </section>
    );
}
